import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import SyncIcon from "@material-ui/icons/Sync";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import {
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip
} from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CatalogProductModal from "../../components/CatalogProductModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../translate/i18n";

const STORAGE_KEY = "catalogWhatsappId";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const items = action.payload;
    const newItems = [];
    items.forEach(item => {
      const idx = state.findIndex(s => s.id === item.id);
      if (idx !== -1) {
        state[idx] = item;
      } else {
        newItems.push(item);
      }
    });
    return [...state, ...newItems];
  }

  if (action.type === "UPDATE") {
    const item = action.payload;
    const idx = state.findIndex(s => s.id === item.id);
    if (idx !== -1) {
      state[idx] = item;
      return [...state];
    }
    return [item, ...state];
  }

  if (action.type === "DELETE") {
    const id = action.payload;
    const idx = state.findIndex(s => s.id === id);
    if (idx !== -1) {
      state.splice(idx, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  selector: {
    minWidth: 180
  },
  thumb: {
    width: 44,
    height: 44,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.12)"
  },
  hint: {
    fontSize: "0.8em",
    opacity: 0.7
  }
}));

const formatPrice = (cents, currency) => {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
    return "—";
  }
  const value = Number(cents) / 100;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "BRL"
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

const Catalog = () => {
  const classes = useStyles();

  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const isAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [products, dispatch] = useReducer(reducer, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [whatsappId, setWhatsappId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [capabilities, setCapabilities] = useState(null);

  // Catálogo só existe em conexões de telefone; e-mail e Instagram não têm.
  const catalogConnections = (whatsApps || []).filter(whatsapp =>
    ["baileys", "cloudapi"].includes(whatsapp.type || "baileys")
  );

  // Primeira visita (ou conexão salva que não existe mais): escolhe a primeira.
  useEffect(() => {
    if (catalogConnections.length === 0) return;
    const stillExists = catalogConnections.some(
      whatsapp => String(whatsapp.id) === String(whatsappId)
    );
    if (!whatsappId || !stillExists) {
      setWhatsappId(String(catalogConnections[0].id));
    }
  }, [catalogConnections, whatsappId]);

  useEffect(() => {
    if (whatsappId) {
      localStorage.setItem(STORAGE_KEY, String(whatsappId));
    }
  }, [whatsappId]);

  const fetchCapabilities = useCallback(async () => {
    if (!whatsappId) return;
    try {
      const { data } = await api.get("/catalog/capabilities", {
        params: { whatsappId }
      });
      setCapabilities(data);
    } catch (err) {
      // Falha aqui não pode travar a listagem: sem capacidades, a tela fica
      // apenas em modo leitura.
      setCapabilities(null);
    }
  }, [whatsappId]);

  const fetchCollections = useCallback(async () => {
    if (!whatsappId) return;
    try {
      const { data } = await api.get("/catalog/collections", {
        params: { whatsappId }
      });
      setCollections(data || []);
    } catch (err) {
      setCollections([]);
    }
  }, [whatsappId]);

  const fetchProducts = useCallback(async () => {
    if (!whatsappId) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/catalog/products", {
        params: { searchParam, pageNumber, whatsappId, collectionId }
      });
      dispatch({ type: "LOAD", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  }, [searchParam, pageNumber, whatsappId, collectionId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, whatsappId, collectionId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchProducts]);

  useEffect(() => {
    fetchCapabilities();
    fetchCollections();
  }, [fetchCapabilities, fetchCollections]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    // Nome do evento igual ao emitido em CatalogController.emit.
    socket.on(`company-${user.companyId}-catalogproduct`, data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.id });
      }
      // A sincronização mexe em lote: recarregar é mais simples (e correto) que
      // tentar reconciliar criados/atualizados/removidos item a item.
      if (data.action === "sync") {
        dispatch({ type: "RESET" });
        setPageNumber(1);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user]);

  const handleSync = async () => {
    if (!whatsappId) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/catalog/sync/${whatsappId}`);
      toast.success(
        i18n.t("catalog.toasts.synced", {
          created: data.created,
          updated: data.updated,
          removed: data.removed
        })
      );
      dispatch({ type: "RESET" });
      setPageNumber(1);
      await fetchCapabilities();
      await fetchCollections();
    } catch (err) {
      toastError(err);
    }
    setSyncing(false);
  };

  const handleOpenModal = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelected(null);
    setModalOpen(false);
  };

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEdit = product => {
    setSelected(product);
    setModalOpen(true);
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/catalog/products/${id}`);
      toast.success(i18n.t("catalog.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchProducts();
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber(prev => prev + 1);
    }
  };

  const canSync = isAdmin && Boolean(capabilities?.canSync);
  const canCreate = isAdmin && Boolean(capabilities?.canCreate);
  const canUpdate = isAdmin && Boolean(capabilities?.canUpdate);
  const canDelete = isAdmin && Boolean(capabilities?.canDelete);
  const showActions = canUpdate || canDelete;

  /** Botão desabilitado explica o porquê, em vez de só ficar cinza. */
  const withReason = (node, reason) =>
    reason ? (
      <Tooltip title={reason}>
        <span>{node}</span>
      </Tooltip>
    ) : (
      node
    );

  return (
    <MainContainer>
      <ConfirmationModal
        title={i18n.t("catalog.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deleting.id)}
      >
        {i18n.t("catalog.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CatalogProductModal
        open={modalOpen}
        onClose={handleCloseModal}
        reload={fetchProducts}
        productId={selected && selected.id}
        whatsappId={whatsappId}
      />
      <MainHeader>
        <Title>{i18n.t("catalog.title")}</Title>
        <MainHeaderButtonsWrapper>
          <FormControl className={classes.selector}>
            <InputLabel>{i18n.t("catalog.connection")}</InputLabel>
            <Select
              value={whatsappId}
              onChange={e => setWhatsappId(e.target.value)}
            >
              {catalogConnections.map(whatsapp => (
                <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                  {whatsapp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {collections.length > 0 && (
            <FormControl className={classes.selector}>
              <InputLabel>{i18n.t("catalog.collection")}</InputLabel>
              <Select
                value={collectionId}
                onChange={e => setCollectionId(e.target.value)}
              >
                <MenuItem value="">{i18n.t("catalog.allCollections")}</MenuItem>
                {collections.map(collection => (
                  <MenuItem key={collection.id} value={String(collection.id)}>
                    {collection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            placeholder={i18n.t("catalog.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
          {isAdmin &&
            withReason(
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSync}
                disabled={!canSync || syncing}
                startIcon={
                  syncing ? <CircularProgress size={16} /> : <SyncIcon />
                }
              >
                {i18n.t("catalog.buttons.sync")}
              </Button>,
              canSync ? null : capabilities?.reasons?.canSync
            )}
          {isAdmin &&
            withReason(
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenModal}
                disabled={!canCreate}
              >
                {i18n.t("catalog.buttons.add")}
              </Button>,
              canCreate ? null : capabilities?.reasons?.canCreate
            )}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("catalog.table.image")}</TableCell>
              <TableCell align="center">{i18n.t("catalog.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("catalog.table.sku")}</TableCell>
              <TableCell align="center">{i18n.t("catalog.table.price")}</TableCell>
              <TableCell align="center">{i18n.t("catalog.table.status")}</TableCell>
              {showActions && (
                <TableCell align="center">{i18n.t("catalog.table.actions")}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {products.map(product => (
                <TableRow key={product.id}>
                  <TableCell align="center">
                    {product.imageUrl ? (
                      <img
                        className={classes.thumb}
                        src={product.imageUrl}
                        alt={product.name}
                        onError={e => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="center">{product.name}</TableCell>
                  <TableCell align="center">
                    {product.retailerId || "—"}
                  </TableCell>
                  <TableCell align="center">
                    {formatPrice(product.priceCents, product.currency)}
                  </TableCell>
                  <TableCell align="center">
                    {product.isHidden && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={i18n.t("catalog.status.hidden")}
                      />
                    )}
                    {/* Produto "crm" nunca chegou ao provedor — o atendente
                        precisa saber que ele não existe no WhatsApp. */}
                    {product.syncSource === "crm" && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="secondary"
                        label={i18n.t("catalog.status.localOnly")}
                      />
                    )}
                    {!product.isHidden && product.syncSource !== "crm" && (
                      <Chip
                        size="small"
                        color="primary"
                        label={i18n.t("catalog.status.published")}
                      />
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell align="center">
                      {canUpdate && (
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(product)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeleting(product);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={showActions ? 6 : 5} />}
            </>
          </TableBody>
        </Table>
        {!loading && products.length === 0 && (
          <div style={{ padding: 16, textAlign: "center" }}>
            <span className={classes.hint}>
              {i18n.t("catalog.empty")}
            </span>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Catalog;
